
import { useState, useEffect } from 'react';
import { useOrchestrationContext } from './useOrchestrationContext';
import { useOrchestrationPipeline } from './useOrchestrationPipeline';
import { useOrchestrationState } from './useOrchestrationState';
import { useOrchestrationInsights } from './useOrchestrationInsights';
import { useActiveAgents } from './useActiveAgents';
import { availableAgents } from '@/utils/a2a/registry/agentRegistry';
import { useA2AProtocol } from '../useA2AProtocol';
import { OrchestrationInsights } from '@/components/ai/types';
import { Message } from '@/components/ai/types';

export function useOrchestration() {
  const orchestrationContext = useOrchestrationContext();
  const [pendingClarification, setPendingClarification] = useState(false);
  const [orchestrationActive, setOrchestrationActive] = useState(false);
  const [orchestrationInsights, setOrchestrationInsights] = useState<OrchestrationInsights | null>(null);
  const { activeAgents, setActiveAgents } = useActiveAgents();
  const { taskComplexity } = useOrchestrationState();
  const { 
    fetchDataAgentData,
    fetchCalendarEvents,
    checkCalendarAvailability
  } = useA2AProtocol();
  
  const {
    processUserInput,
    userResponses
  } = useOrchestrationPipeline({
    setActiveAgents,
    setOrchestrationInsights,
    setPendingClarification
  });

  // Add method to fetch today's priorities directly
  const fetchTodaysPriorities = async () => {
    try {
      const tasks = await fetchDataAgentData('fetchAllTasks', {
        status: 'open',
        priority: 'high',
        dueDateRange: `[${new Date().toISOString()},)`,
        limit: 10
      });

      return tasks;
    } catch (error) {
      console.error('Error fetching today\'s priorities:', error);
      return [];
    }
  };
  
  // Add method to fetch upcoming calendar events
  const fetchUpcomingEvents = async (days = 7) => {
    try {
      const now = new Date();
      const endDate = new Date();
      endDate.setDate(now.getDate() + days);
      
      const events = await fetchCalendarEvents(now, endDate);
      return events;
    } catch (error) {
      console.error('Error fetching upcoming events:', error);
      return [];
    }
  };
  
  // Add method to find suitable meeting slots
  const findAvailableMeetingSlots = async (params: {
    startDate: Date,
    endDate: Date,
    durationMinutes: number,
    participants?: string[]
  }) => {
    try {
      const availableSlots = await checkCalendarAvailability(
        params.startDate, 
        params.endDate, 
        params.durationMinutes
      );
      return availableSlots;
    } catch (error) {
      console.error('Error finding available meeting slots:', error);
      return [];
    }
  };

  useEffect(() => {
    // When processing is complete, set orchestration to inactive
    if (!orchestrationContext.isLoading && orchestrationActive) {
      const timeout = setTimeout(() => {
        setOrchestrationActive(false);
      }, 500);
      return () => clearTimeout(timeout);
    }
  }, [orchestrationContext.isLoading, orchestrationActive]);

  return {
    // State
    orchestrationActive,
    pendingClarification,
    orchestrationInsights,
    activeAgents,
    taskComplexity,
    availableAgents,
    userResponses,
    
    // Actions
    processUserInput,
    setOrchestrationActive,
    setActiveAgents,
    fetchTodaysPriorities,
    fetchUpcomingEvents,
    findAvailableMeetingSlots
  };
}
